import React from "react";
import { BiSearch, BiCodeAlt, BiLayer, BiCustomize, BiBulb, BiRocket } from "@acme/ui";
import { FooterSite, HeaderSite, LandingFaq, LandingFeatures, LandingHero, LandingModelSupport } from "@acme/feature";
import Head from "next/head";

const BetterChatgptUiPage = () => {
    const features = [
        {
            title: "Organize with Folders",
            description: "Keep your ChatGPT conversations organized with custom folders and tags.",
            icon: <BiLayer className="text-3xl" />,
            colSpan: "md:col-span-2",
            bg: "bg-blue-50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-800/20"
        },
        {
            title: "Prompt Library",
            description: "Save your best prompts and reuse them instantly.",
            icon: <BiCodeAlt className="text-3xl" />,
            colSpan: "md:col-span-1",
            bg: "bg-purple-50 dark:bg-purple-900/10 border-purple-100 dark:border-purple-800/20"
        },
        {
            title: "Smart Search",
            description: "Find any conversation or note with our Spotlight-like search.",
            icon: <BiSearch className="text-3xl" />,
            colSpan: "md:col-span-1",
            bg: "bg-emerald-50 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-800/20"
        },
        {
            title: "Customization",
            description: "Tailor the ChatGPT prompts and behavior to fit your unique workflow.",
            icon: <BiCustomize className="text-3xl" />,
            colSpan: "md:col-span-2",
            bg: "bg-pink-50 dark:bg-pink-900/10 border-pink-100 dark:border-pink-800/20"
        },
        {
            title: "Custom Tools and MVPs",
            description: "Extend ChatGPT with your own tools, APIs, and micro-plugins.",
            icon: <BiBulb className="text-3xl" />,
            colSpan: "md:col-span-2",
            bg: "bg-yellow-50 dark:bg-yellow-900/10 border-yellow-100 dark:border-yellow-800/20"
        },
        {
            title: "Agent",
            description: "Create multi-step AI workflows that can think, plan, and execute tasks.",
            icon: <BiRocket className="text-3xl" />,
            colSpan: "md:col-span-1",
            bg: "bg-indigo-50 dark:bg-indigo-900/10 border-indigo-100 dark:border-indigo-800/20"
        },
    ];

    const faqList = [
        { "question": "Is this a better UI for ChatGPT?", "answer": "Yes, DapperGPT offers features not found in the standard interface, like folders, search, and prompt libraries." },
        { "question": "Do I need my own API key?", "answer": "Yes, you use your own OpenAI API key. This means you pay OpenAI directly for usage, often cheaper than a Plus subscription for light use." },
        { "question": "Is my data safe?", "answer": "Your API key is stored locally on your device. We do not proxy your requests or store your chat data on our servers by default." },
        { "question": "Can I organize chats into folders?", "answer": "Yes. Create folders and tags to keep work, personal, and client conversations separate." },
        { "question": "Can I search chat history?", "answer": "Yes, DapperGPT includes fast search so you can find prompts, answers, and old conversations instantly." },
        { "question": "Is this an alternative to ChatGPT Plus?", "answer": "It can be. If you have an API key, you can pay per usage and often save money depending on your volume." }
    ]

    return (
        <>
            <Head>
                <title>Better ChatGPT UI - DapperGPT</title>
                <meta name="description" content="A better ChatGPT UI for power users. Use your own API key, organize chats with folders, search history, and reusable prompts—plus voice and image support." />
                <meta property="og:title" content="Better ChatGPT UI - DapperGPT" />
                <meta property="og:description" content="The ultimate interface for ChatGPT power users." />
            </Head>
            <div className="bg-white dark:bg-gray-900 min-h-screen flex flex-col font-sans text-gray-900 dark:text-gray-100 selection:bg-blue-500/30 transition-colors duration-300">
                <HeaderSite containerClassName="!max-w-7xl px-6" />

                <main className="flex-grow">
                    <LandingHero
                        title="A Better ChatGPT UI"
                        subtitle="Stop wrestling with limits. Use your own OpenAI API key in a professional, feature-rich interface designed for power users."
                        badge="ChatGPT Interface"
                        heroImage="/page/better-chatgpt-ui.png"
                        color="green"
                    />
                    <LandingFeatures
                        title="Why it's better than the default UI"
                        subtitle="Designed for power users who want more control."
                        features={features}
                    />
                    <LandingModelSupport title="Powered by Leading Models">
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold font-serif text-gray-900 dark:text-gray-100">OpenAI</span></div>
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold text-gray-900 dark:text-gray-100">Anthropic</span></div>
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold font-sans text-gray-900 dark:text-gray-100">Gemini</span></div>
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold font-mono text-gray-900 dark:text-gray-100">Mistral</span></div>
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold font-sans text-gray-900 dark:text-gray-100">Grok</span></div>
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold font-mono text-gray-900 dark:text-gray-100">Llama</span></div>
                    </LandingModelSupport>
                    <LandingFaq items={faqList} />
                </main>

                <FooterSite />
            </div>
        </>
    );
};

export default BetterChatgptUiPage;
